import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, FileWarning, Loader2 } from 'lucide-react';
import { checkInvalidHtml, type CheckInvalidHtmlResponse, type InvalidSlide } from '../../lib/api';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  planId: number;
};

export const CheckHtmlModal: React.FC<Props> = ({ isOpen, onClose, planId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CheckInvalidHtmlResponse | null>(null);
  const [expandedSlide, setExpandedSlide] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen && planId) {
      setIsLoading(true);
      setError(null);
      setData(null);
      
      checkInvalidHtml(planId)
        .then(res => {
          setData(res.data);
        })
        .catch(err => {
          console.error('Check HTML error:', err);
          setError(err.message || '检查失败，请稍后重试');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, planId]);

  if (!isOpen) return null;

  const getReasonBadge = (reason: string) => {
    if (reason.includes('空')) {
      return <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">HTML为空</span>;
    }
    if (reason.includes('DOCTYPE') && reason.includes('</html>')) {
      return <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">缺少开头和结尾</span>;
    }
    if (reason.includes('DOCTYPE')) {
      return <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">缺少DOCTYPE</span>;
    }
    if (reason.includes('</html>')) {
      return <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">缺少结尾标签</span>;
    }
    return <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">{reason}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[85vw] max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <FileWarning size={20} className="text-orange-500" />
            <h3 className="text-lg font-semibold text-gray-900">HTML代码合规检查</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {isLoading && (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 size={32} className="animate-spin text-blue-500 mb-3" />
              <p className="text-gray-500">正在检查HTML代码...</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <AlertTriangle size={32} className="mb-3" />
              <p>{error}</p>
              <button
                onClick={() => {
                  setIsLoading(true);
                  setError(null);
                  checkInvalidHtml(planId)
                    .then(res => setData(res.data))
                    .catch(err => setError(err.message || '检查失败'))
                    .finally(() => setIsLoading(false));
                }}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                重试
              </button>
            </div>
          )}

          {!isLoading && !error && data && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-gray-700">{data.total_slides}</div>
                  <div className="text-sm text-gray-500">总Slide数</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{data.valid_count}</div>
                  <div className="text-sm text-green-600">合规</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{data.invalid_count}</div>
                  <div className="text-sm text-red-600">不合规</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {data.total_slides > 0 ? Math.round((data.valid_count / data.total_slides) * 100) : 0}%
                  </div>
                  <div className="text-sm text-blue-600">合规率</div>
                </div>
              </div>

              {data.invalid_count === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-green-600">
                  <CheckCircle size={48} className="mb-3" />
                  <p className="text-lg font-medium">所有HTML代码都合规！</p>
                </div>
              ) : (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    不合规的Slide列表 ({data.invalid_count}个)
                  </h4>
                  <div className="space-y-2">
                    {data.invalid_slides.map((slide) => (
                      <div key={slide.slide_id} className="border rounded-lg overflow-hidden">
                        <div
                          className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                          onClick={() => setExpandedSlide(expandedSlide === slide.slide_id ? null : slide.slide_id)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">
                              Slide #{slide.slide_index}
                            </span>
                            {getReasonBadge(slide.reason)}
                            {slide.is_final ? (
                              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">final</span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">draft</span>
                            )}
                            {slide.version && (
                              <span className="text-xs text-gray-400">{slide.version}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">ID: {slide.slide_id}</span>
                            <svg
                              className={`w-4 h-4 text-gray-400 transition-transform ${expandedSlide === slide.slide_id ? 'rotate-180' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        
                        {expandedSlide === slide.slide_id && slide.html_preview && (
                          <div className="p-3 bg-gray-900 text-gray-100">
                            <div className="text-xs text-gray-400 mb-2">HTML预览 (前500字符):</div>
                            <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono">
                              {slide.html_preview?.substring(0, 500)}
                              {slide.html_preview && slide.html_preview.length > 500 && '...'}
                            </pre>
                          </div>
                        )}
                        
                        {expandedSlide === slide.slide_id && !slide.html_preview && (
                          <div className="p-3 bg-gray-100 text-gray-500 text-sm">
                            无HTML内容
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors text-sm"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};
